import { t } from 'i18next'

import i18n from '@/i18n'
import type {
  AddOrderProducts,
  CallDetail,
  CreateOrder,
  EditOrderDetailRequestData,
  EditOrderProduct,
  Order,
  OrderClient,
  OrderData,
  OrderDeliveryService,
  OrderDetail,
  OrderPrice,
  OrderPriceRequestData,
  OrderProduct,
  OrderProductDetail,
  OrderProductWarehouse,
  ServerResponse,
  Table,
} from '@/types'

import { api } from '../../api'

export const ordersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrders: builder.query<Table<Order[]>, any>({
      query: (params) => ({
        url: 'orders',
        params,
      }),
      transformResponse: (response: ServerResponse<Table<Order[]>>) => ({
        ...response.data,
        data: response.data.data.map((i) => ({ ...i, name: `${t('orderNumber')} №${i.id}` })),
      }),
      providesTags: ['ordersList'],
    }),
    getOrderDetail: builder.query<OrderDetail, number>({
      query: (id) => ({
        url: `orders/${id}`,
        params: {
          lang: i18n.language,
        },
      }),
      transformResponse: (response: ServerResponse<OrderDetail>) => response.data,
      providesTags: ['orderDetail'],
    }),
    createOrder: builder.mutation<ServerResponse<number>, CreateOrder>({
      query: (data) => ({
        url: 'orders',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ordersList', 'carts']),
    }),
    editOrder: builder.mutation<any, { id: number; data: EditOrderDetailRequestData }>({
      query: ({ id, data }) => ({
        url: `orders/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['orderDetail', 'ordersList']),
    }),
    changeOrderStatus: builder.mutation<any, { id: number; data: { orderStatusId: number } }>({
      query: ({ id, data }) => ({
        url: `orders/${id}/status`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['orderDetail', 'ordersList']),
    }),
    getOrderPrice: builder.mutation<OrderPrice, OrderPriceRequestData>({
      query: (data) => ({
        url: 'orders/price',
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ServerResponse<{ totalInfo: OrderPrice }>) => response.data.totalInfo,
    }),
    addProductsToOrder: builder.mutation<Array<OrderProduct>, { id: number; data: AddOrderProducts }>({
      query: ({ id, data }) => ({
        url: `orders/${id}/products`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ServerResponse<{ orderProducts: Array<OrderProduct> }>) => response.data.orderProducts,
      invalidatesTags: (_, error) => (error ? [] : ['orderProductList']),
    }),
    editOrderProduct: builder.mutation<Array<OrderProduct>, { id: number; productId: number; data: EditOrderProduct }>({
      query: ({ id, productId, data }) => ({
        url: `orders/${id}/products/${productId}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ServerResponse<{ orderProducts: Array<OrderProduct> }>) => response.data.orderProducts,
      invalidatesTags: (_, error) => (error ? [] : ['orderProductList']),
    }),
    deleteProductFromOrder: builder.mutation<any, { id: number; productId: number }>({
      query: ({ id, productId }) => ({
        url: `orders/${id}/products/${productId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (error ? [] : ['orderProductList']),
    }),
    getOrderProduct: builder.query<OrderProductDetail, { id: number; productId: number }>({
      query: ({ id, productId }) => ({
        url: `orders/${id}/products/${productId}`,
      }),
      transformResponse: (response: ServerResponse<{ product: OrderProductDetail }>) => response.data.product,
      providesTags: ['orderProductList'],
    }),
    getOrderProductList: builder.query<Table<OrderProductWarehouse[]>, any>({
      query: (params) => ({
        url: 'orders/warehouses/products',
        params,
      }),
      transformResponse: (response: ServerResponse<Table<OrderProductWarehouse[]>>) => response.data,
      providesTags: ['orderProductList'],
    }),
    searchOrderClients: builder.query<any, { search: string; orderId?: number }>({
      query: (params) => ({
        url: '/orders/clients/search',
        params,
      }),
      transformResponse: (response: ServerResponse<Table<OrderClient[]>>) => ({
        ...response.data,
        data: response.data.data.length ? response.data.data.map((i) => ({ search: i.customer.fullName, value: i })) : [],
      }),
      providesTags: ['allClients'],
    }),
    getDataForCreateOrder: builder.query<OrderData, any>({
      query: () => ({
        url: 'orders/create',
      }),
      transformResponse: (response: ServerResponse<OrderData>) => response.data,
      providesTags: ['ordersStatuses', 'countries'],
    }),
    getOrderDeliveryServices: builder.query<OrderDeliveryService[], { countryId: number }>({
      query: (params) => ({
        url: 'orders/delivery_services',
        params,
      }),
      transformResponse: (response: ServerResponse<OrderDeliveryService[]>) => response.data,
      providesTags: ['deliveryServices'],
    }),
    getOrderPaymentServices: builder.query<{ name: string; value: number }[], { countryId: number; deliveryServiceId?: number }>({
      query: (params) => ({
        url: 'orders/payment_services',
        params,
      }),
      transformResponse: (response: ServerResponse<{ name: string; value: number }[]>) => response.data,
      providesTags: ['deliveryServices'],
    }),
    getOrderProductsTable: builder.query<Array<OrderProduct>, any>({
      query: (params) => ({
        url: 'orders/products',
        params,
      }),
      transformResponse: (response: ServerResponse<{ products: Array<OrderProduct> }>) => response.data.products,
    }),
    deleteOrder: builder.mutation<any, number>({
      query: (id) => ({
        url: `orders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ordersList']),
    }),
    searchByPhone: builder.query<CallDetail, { phone: string }>({
      query: (params) => ({
        url: 'orders/search_by_phone',
        params,
      }),
      transformResponse: (response: ServerResponse<CallDetail>) => response.data,
    }),
    recordCall: builder.mutation<any, { phone: string; orderId?: number }>({
      query: (body) => ({
        url: 'orders/call_ended',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['orderDetail'],
    }),
    getCallRecording: builder.query<string, { id: number }>({
      query: ({ id }) => ({
        url: `recordings/${id}`,
        responseHandler: async (response) => URL.createObjectURL(await response.blob()),
        cache: 'no-cache',
      }),
    }),
  }),
})

export const {
  useGetOrdersQuery,
  useLazyGetOrdersQuery,
  useGetOrderDetailQuery,
  useCreateOrderMutation,
  useEditOrderMutation,
  useChangeOrderStatusMutation,
  useGetOrderPriceMutation,
  useAddProductsToOrderMutation,
  useEditOrderProductMutation,
  useDeleteProductFromOrderMutation,
  useGetOrderProductQuery,
  useGetOrderProductListQuery,
  useLazyGetOrderProductListQuery,
  useLazySearchOrderClientsQuery,
  useGetDataForCreateOrderQuery,
  useGetOrderDeliveryServicesQuery,
  useGetOrderPaymentServicesQuery,
  useLazyGetOrderProductsTableQuery,
  useDeleteOrderMutation,
  useSearchByPhoneQuery,
  useRecordCallMutation,
  useLazyGetCallRecordingQuery,
} = ordersApi
